import utils from './utils';
import pinnedMessageTemplate from '../templates/pinned-message.html';
import commentTemplate from '../templates/comment.html';
import commentReplyTemplate from '../templates/comment-reply.html';

const defaultMessage = {
  author: {
    name: '',
    avatarUrl: ''
  },
  date: '',
  text: ''
};

export default class MessageList {
  constructor({
    container,
    pinnedMessageContainer,
    hideMessages,
    chatApi,
    getGeneratedAvatarForUser
  }) {
    this.container = container;
    this.pinnedMessageContainer = pinnedMessageContainer;
    this.messages = [];
    this.messageIds = new Set();
    // Скрывать ли сообщения по умолчанию
    this.hideMessages = !!hideMessages;
    this.chatApi = chatApi;
    this.getGeneratedAvatarForUser = getGeneratedAvatarForUser;

    /* Скрываем кнопки ответа при инициализации MessageList
       Далее отображение/скрытие происходит при событиях chatApi 'chatAuthorized' | 'deauthorized' */
    this.hideReplyButtons();
    this.setListeners();
  }

  get isEmpty() {
    return this.messages.length === 0;
  }

  get hideMessages() {
    return this._hideMessages;
  }

  set hideMessages(value) {
    this._hideMessages = value;
  }

  setListeners() {
    this.chatApi.on('chatAuthorized', () => this.showReplyButtons());
    this.chatApi.on('deauthorized', () => this.hideReplyButtons());
  }

  getMessageById(messageId) {
    return this.messages.find(message => message.id === messageId);
  }

  hideReplyButtons() {
    this.container.classList.add('vjs-chat-messanger_hide-reply-buttons');
  }

  showReplyButtons() {
    this.container.classList.remove('vjs-chat-messanger_hide-reply-buttons');
  }

  toggleMessageVisibility(messageId, published) {
    let messageEl = this.container.querySelector(`#comment-${messageId}`);
    if (messageEl) {
      messageEl.classList[published ? 'remove' : 'add']('vjs-hidden');
    }
  }

  toggleMessageReplyVisibility(replyMessageId, published) {
    const replyMessageEl = this.container.querySelector(`.vjs-chat-comment__reply[data-original-message-id="comment-${replyMessageId}"]`);
    if (replyMessageEl) {
      replyMessageEl.classList[published ? 'remove' : 'add']('vjs-hidden');
    }
  }

  togglePinnedMessageVisibility(published) {
    this.pinnedMessageContainer.classList[published ? 'remove' : 'add']('vjs-hidden');
  }

  updateMessageDataPublished(messageId, published) {
    let messageEl = this.container.querySelector(`#comment-${messageId}`);
    if (messageEl) {
      messageEl.dataset.published = published ? "published" : "unpublished";
    }
  }

  prepareMessage(message) {
    message = videojs.mergeOptions(defaultMessage, message);
    message.date = utils.formatDateTime(message.time);
    return message;
  }
  /**
   * @param {object} message Сообщение
   * @param {'visible'|'hide'} [visibility] Определяет будет ли отображаться сообщение
   * @description добавляет сообщение
  */
  addMessage(message, visibility = 'visible') {
    message = this.prepareMessage(message);
    // Если сообщение уже было в списке - не добавляем
    if (this.messageIds.has(message.id)) {
      return;
    }
    this.renderMessage(message);
    message.el = this.container.querySelector(`#comment-${message.id}`);
    this.toggleMessageVisibility(message.id, visibility !== 'hide')
    this.messages.push(message);
    this.messageIds.add(message.id);
  }

  cleanPinnedMessage() {
    this.pinnedMessageContainer.removeAttribute("data-original-message-id");
    this.pinnedMessageContainer.innerHTML = "";
  }

  getMessageTemplate(message) {
    let commentReplyHTML;
    const originalReplyMessage = message.replyFor && this.getMessageById(message.replyFor.id);
    const isAuthorOfOriginalReplyMessageBlocked = originalReplyMessage && Boolean(originalReplyMessage.author.blocked);

    if (message.replyFor && originalReplyMessage && !isAuthorOfOriginalReplyMessageBlocked) {
      commentReplyHTML = commentReplyTemplate({ message: message.replyFor });
    }

    const commentHTML = commentTemplate({
      message,
      hideMessages: this.hideMessages,
      commentReplyHTML,
    });

    return commentHTML;
  }

  renderPinnedMessage(message) {
    this.formatMessageText(message);
    this.pinnedMessageContainer.innerHTML = pinnedMessageTemplate({ message });
  }

  renderMessage(message) {
    this.formatMessageText(message);

    this.container.insertAdjacentHTML('beforeend', this.getMessageTemplate(message));

    this.container
      .querySelector(`#comment-${message.id} img.vjs-chat-avatar`)
      .onerror = event => {
        event.target.src = this.getGeneratedAvatarForUser(message.author.name);
      };

    // Отображаем кнопку ответа для нового сообщение, если пользователь авторизован (по умолчанию скрыта)
    if (this.chatApi.userAuthorized) {
      this.container
        .querySelector(`#comment-${message.id} button.vjs-chat-comment__reply-button`)
        .classList
        .remove('vjs-hidden');
    }
  }

  addMessages(messages) {
    messages.forEach(message => this.addMessage(message));
  }

  clearMessages() {
    this.messages = [];
    this.messageIds = new Set();
    this.container.innerHTML = '';
  }

  hasOriginalPinnedMessage(messageIds) {
    return messageIds.some((id) => {
      return this.isOriginalPinnedMessageId(id);
    });
  }

  isOriginalPinnedMessageId(messageId) {
    const originalPinnedMessageId = this.pinnedMessageContainer.getAttribute("data-original-message-id");
    if (!originalPinnedMessageId) {
      return false;
    }
    return `comment-${messageId}` === originalPinnedMessageId;
  }

  showAll() {
    this.messages.forEach(message => {
      if (message.el.dataset.published === 'unpublished') return;
      message.el.classList.remove('vjs-hidden');
    });
  }

  showForTime(time) {
    if (isNaN(time)) {
      return;
    }

    this.messages.forEach(message => {
      if (message.time <= time && message.el.dataset.published === 'published') {
        message.el.classList.remove('vjs-hidden');
      } else {
        message.el.classList.add('vjs-hidden');
      }
    });
  }

  messageIdByTime(time) {
    const message = this.messages?.find(message => message.time > time);
    return message !== undefined ? message.id : this.messages?.[this.messages.length - 1].id;
  }

  formatMessageText(message) {
    message.text = utils.escapeHTMLTags(message.text);

    if (message.moderator) {
      message.text = utils.replaceBracketUrlsWithAnchors(message.text);
    }

    message.text = utils.replaceUrlsWithAnchors(message.text, 'vjs-chat-comment__text-link');
  }
}
